import { Link, useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconArchive } from '@components/icons/archive';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconCopy } from '@components/icons/copy';
import { IconDownload } from '@components/icons/download';
import { IconJobboard } from '@components/icons/jobboard';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconPublish } from '@components/icons/publish';
import { IconShare } from '@components/icons/share';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconUnpublish } from '@components/icons/unpublish';
import { Menu } from '@components/menu';
import { MenuSeparator } from '@components/menu/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import {
  useArchiveJob,
  useDuplicateJob,
  useJobActionFailureSubtitle,
  useMoveToDrafts,
  usePublishJob,
  useUnpublishJob,
} from 'api/jobs/hooks';
import { LocationState } from 'components/navigation/create-job';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { Job, JobLanguage } from 'types/jobs';

import { getMissingApplicationFormTranslations, getMissingJobPostingTranslations } from './helpers';

type ActionsProps = {
  job?: Job;
  hasUnsavedChanges: boolean;
  onSave: () => void;
  onCancel: () => void;
  noLangEnabled?: boolean;
  dataCyCancel?: string;
  dataCySave?: string;
  dataCyPublish?: string;
  isSaving?: boolean;
};

export const Actions = (props: ActionsProps) => {
  const {
    hasUnsavedChanges,
    onSave,
    onCancel,
    job,
    noLangEnabled,
    dataCyCancel,
    dataCySave,
    dataCyPublish,
    isSaving = false,
  } = props;
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(topBarActionsLocale);
  const { showModal } = useModal();
  const { lang } = useStore();
  const { hasRolePermission } = usePermissions();
  const publishJob = usePublishJob();
  const location = useLocation();
  const duplicateJob = useDuplicateJob();
  const unPublishJob = useUnpublishJob();
  const archiveJob = useArchiveJob();
  const moveJobToDrafts = useMoveToDrafts();
  const failureSubtitle = useJobActionFailureSubtitle();
  const navigate = useNavigate();
  const { from } = (location.state as LocationState) || { from: '/' };

  const isPublished = job?.isPublished;
  const isExpired = job?.isExpired;

  const handlePublish = () => {
    if (job) {
      if (isPublished) {
        onSave();
      } else {
        publishJob.mutate({ jobId: job.id, title: job.title });
        showModal('JOB_PUBLISHED', {
          _container: {
            closeOnOverlayClickOrEsc: false,
            autoWidth: true,
            minWidth: '500px',
          },
          job,
          t,
        });
      }
    }
  };

  const ispublishedDisabled =
    (hasUnsavedChanges && !isPublished) || (!hasUnsavedChanges && isPublished) || noLangEnabled;

  let anyMissingTranslations = false;
  if (job) {
    const jobLanguages = job.languages.map((l: JobLanguage) => l.language);
    const missingTranslationsJobPosting =
      job && getMissingJobPostingTranslations(job, jobLanguages);
    const missingTranslationsApplicationForm =
      job && getMissingApplicationFormTranslations(job, jobLanguages);

    anyMissingTranslations = Object.values(missingTranslationsJobPosting).some(
      (value) =>
        value === true ||
        Object.values(missingTranslationsApplicationForm).some((value) => value === true),
    );
  }

  const exportJobModal = () => {
    // @ts-expect-error
    showModal('EXPORT_JOB', {
      _container: {
        autoWidth: false,
        closeOnOverlayClickOrEsc: false,
      },
      job: job,
    });
  };

  const shareJobModal = () => {
    if (job) {
      showModal('SHARE_JOB_FROM_CREATE_MODAL', {
        _container: {
          autoWidth: false,
          closeOnOverlayClickOrEsc: false,
        },
        job: job,
      });
    }
  };

  const archive = (jobId: number) => {
    archiveJob.mutate({ jobId, title: job?.title });
    return true;
  };

  const archiveJobModal = () => {
    if (job) {
      // @ts-expect-error
      showModal('CONFIRM_MODAL', {
        _container: {
          minWidth: '200px',
          autoWidth: true,
          closeOnOverlayClickOrEsc: false,
        },
        data: job,
        confirm: archive,
        buttonIcon: <IconArchive />,
        headerText: t('archiveModalHeader'),
        mainText: t('archiveModalContent', { jobTitle: job.title }),
        confirmText: t('archive'),
      });
    }
  };

  const isDraft = !job?.isPublished && !job?.isArchived && !job?.isExpired;

  const DraftWrapper = isDraft && !hasUnsavedChanges ? StyledDraftText : Stack;

  const onDuplicateSuccess = () => {
    job && showSuccess(t('duplicateSuccess', { jobTitle: job.title }));
  };

  const onDuplicateError = (error: unknown) => {
    job &&
      showError(t('duplicateError', { jobTitle: job.title }), {
        subtitle: failureSubtitle(error),
      });
  };

  const jobStatusText = job?.isArchived
    ? t('achivedIndicator')
    : isExpired && isPublished
      ? t('expiredIndicator')
      : isPublished
        ? t('published')
        : t('draft');

  const jobStatusDate = job?.isArchived
    ? job?.dateArchived
    : isExpired && isPublished
      ? job?.deadline
      : isPublished
        ? job?.datePublished
        : undefined;

  return (
    <Stack space={6} orientation="horizontal">
      <div>
        <Stack align="end">
          <DraftWrapper orientation="horizontal" align="center">
            <IconStatusIndicator color={isPublished && !isExpired ? 'green200' : 'purple50'} />
            <Text
              font="regular14"
              color={isPublished && !isExpired ? 'green200' : 'purple50'}
              text={jobStatusText}
            />
          </DraftWrapper>
        </Stack>
        <Stack align="end">
          {hasUnsavedChanges && !jobStatusDate ? (
            <Text font="regular14" color="purple50" text={t('unsavedChanges')} />
          ) : (
            <Text
              font="regular14"
              color="purple50"
              text={jobStatusDate && abbreviateDate(jobStatusDate, lang, 'datetime')}
            />
          )}
        </Stack>
      </div>
      <Stack space={2} orientation="horizontal">
        <Button
          variant="secondary"
          text={t('cancel')}
          onClick={() => onCancel()}
          disabled={!hasUnsavedChanges}
          dataCy={dataCyCancel}
        />
        {(!isPublished || isExpired) && (
          <Button
            variant="primary-blue"
            text={t('save')}
            onClick={() => onSave()}
            disabled={!hasUnsavedChanges || isSaving}
            tooltip={!hasUnsavedChanges ? t('noNewChanges') : undefined}
            dataCy={dataCySave}
          />
        )}
        <Button
          variant="primary"
          text={isPublished && !isExpired ? t('publishCanges') : t('publish')}
          iconLeft={<IconPublish />}
          disabled={
            ispublishedDisabled ||
            !job ||
            (anyMissingTranslations && !isPublished) ||
            isSaving ||
            isExpired
          }
          dataCy={dataCyPublish}
          onClick={() => {
            handlePublish();
          }}
          tooltip={
            anyMissingTranslations
              ? t('missingTranslations')
              : isPublished && !hasUnsavedChanges
                ? t('noNewChanges')
                : noLangEnabled
                  ? t('nolanguageEnabled')
                  : undefined
          }
        />
      </Stack>
      <Stack space={2} orientation="horizontal">
        <IconButton
          icon={<IconOpenPreview color="purple50" />}
          variant="secondary-purple"
          disabled={!job || hasUnsavedChanges}
          onClick={() => window.open(`/job/${job?.id}/preview`, '_blank')}
          tooltip={t('preview')}
        />
        {job ? (
          <Menu
            buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'secondary', isDisabled: !job }}
          >
            {hasRolePermission('job:duplicate') && (
              <Menu.Item
                icon={<IconCopy />}
                onSelect={() =>
                  duplicateJob.mutate({
                    jobId: job?.id,
                    onSuccess: onDuplicateSuccess,
                    onError: onDuplicateError,
                  })
                }
                label={t('duplicateJob')}
              />
            )}
            {hasRolePermission('job:export') && (
              <Menu.Item
                icon={<IconDownload />}
                onSelect={exportJobModal}
                label={t('exportApplicants')}
              />
            )}
            <Menu.Item
              icon={<IconShare />}
              onSelect={() => shareJobModal()}
              label={t('share')}
              isDisabled={job?.isArchived || !job?.isPublished}
            />
            <Menu.Item
              icon={<IconJobboard />}
              onSelect={() => navigate(`/jobs/${job.id}/promote`)}
              label={t('postJobBoard')}
              isDisabled={!job?.isPublished}
            />
            <MenuSeparator />
            {job?.isPublished && !job?.isArchived && (
              <Menu.Item
                icon={<IconUnpublish />}
                onSelect={() => unPublishJob.mutate({ jobId: job.id, title: job.title })}
                label={t('unpublish')}
              />
            )}
            {job?.isArchived && (
              <Menu.Item
                icon={<IconMove />}
                onSelect={() => moveJobToDrafts.mutate({ jobId: job.id, title: job.title })}
                label={t('moveToDraft')}
              />
            )}
            {!job?.isArchived && (
              <Menu.Item icon={<IconArchive />} onSelect={archiveJobModal} label={t('archive')} />
            )}
          </Menu>
        ) : (
          <IconButton icon={<IconMoreVertical />} variant="secondary-purple" disabled />
        )}
        <Link to={from}>
          <IconButton icon={<IconCloseRegular />} variant="secondary-purple" />
        </Link>
      </Stack>
    </Stack>
  );
};

const StyledDraftText = styled(Stack)({
  paddingTop: '0.5rem',
});

const topBarActionsLocale = {
  en: {
    draft: 'Draft',
    published: 'Published',
    unsavedChanges: 'Unsaved changes',
    publish: 'Publish',
    publishCanges: 'Publish changes',
    noNewChanges: 'No new changes',
    cancel: 'Cancel',
    save: 'Save',
    nolanguageEnabled: 'No language enabled',
    duplicateJob: 'Duplicate job',
    exportApplicants: 'Export applicants',
    share: 'Share',
    postJobBoard: 'Post to job board',
    unpublish: 'Unpublish',
    archive: 'Archive',
    archiveModalHeader: 'Archive job',
    archiveModalContent: 'Are you sure you want to archive {jobTitle}?',
    duplicateSuccess: 'Successfully duplicated {jobTitle} and saved as draft.',
    duplicateError: 'Error while duplicating {jobTitle}. Please contact 50skills support.',
    expiredIndicator: 'Expired / Deadline passed',
    achivedIndicator: 'Archived',
    moveToDraft: 'Move to drafts',
    missingTranslations: 'Missing translations',
    preview: 'Preview',
  },
  is: {
    draft: 'Drög',
    published: 'Í loftinu',
    unsavedChanges: 'Óvistaðar breytingar',
    publish: 'Birta',
    publishCanges: 'Birta breytingar',
    noNewChanges: 'Engar nýjar breytingar',
    cancel: 'Hætta við',
    save: 'Vista',
    nolanguageEnabled: 'Engin tungumál virk',
    duplicateJob: 'Afrita starf',
    exportApplicants: 'Hlaða niður umsækjendum',
    share: 'Deila',
    postJobBoard: 'Birta á starfatorgi',
    unpublish: 'Taka úr birtingu',
    archive: 'Setja í geymslu',
    archiveModalHeader: 'Setja starf í geymslu',
    archiveModalContent: 'Ertu viss um að þú viljir setja starfið: {jobTitle} í geymslu?',
    duplicateSuccess: 'Starf {jobTitle} hefur verið afritað og vistað í drög.',
    duplicateError:
      'Ekki tókst að afrita starf {jobTitle}. Vinsamlegast hafið samband við 50skills.',
    expiredIndicator: 'Útrunnið / Umsóknarfrestur er liðinn',
    achivedIndicator: 'Í geymslu',
    moveToDraft: 'Færa í drög',
    missingTranslations: 'Vantar þýðingar',
    preview: 'Forskoðun',
  },
};
